/**
 * Custom Kanban Board
 *
 * Professional, fully customizable Kanban board with:
 * - Responsive column layout that uses available space
 * - Resizable columns with drag handles
 * - Inline column rename
 * - Filter-based or stage-based columns
 * - Customizable card display
 * - Multiple saved views
 * - Drag-drop cards between columns
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Typography,
  Button,
  Input,
  Dropdown,
  Space,
  Spin,
  Empty,
  Badge,
  Tooltip,
  message,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  MoreOutlined,
  SettingOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import type { ProcessInstance } from '@/types';
import type { KanbanView, KanbanColumn, FilterConfig } from '@api/kanban';
import { kanbanApi, COLUMN_COLORS } from '@api/kanban';
import CustomKanbanCard from './CustomKanbanCard';
import './CustomKanban.css';

const { Text, Title } = Typography;

interface CustomKanbanBoardProps {
  view: KanbanView;
  items: ProcessInstance[];
  loading?: boolean;
  onItemClick: (item: ProcessInstance) => void;
  onItemMove?: (itemId: string, fromColumnId: string, toColumnId: string) => Promise<void>;
  onAddItem?: (columnId: string) => void;
  onViewChange?: () => void;
}

// Match items to columns based on filter config
function matchesFilter(item: ProcessInstance, filter: FilterConfig | null | undefined): boolean {
  if (!filter) return true;

  // Handle AND/OR combinations
  if (filter.and) {
    return filter.and.every(f => matchesFilter(item, f));
  }
  if (filter.or) {
    return filter.or.some(f => matchesFilter(item, f));
  }

  const { field, operator, value } = filter;
  if (!field) return true;

  // Get the item's field value
  let itemValue: any;
  switch (field) {
    case 'status':
      itemValue = item.status;
      break;
    case 'categoryId':
      itemValue = item.template?.categoryId;
      break;
    case 'assignedUserId':
      itemValue = item.assignedUserId;
      break;
    case 'tags':
      itemValue = item.template?.tags?.map(t => t.id) || [];
      break;
    case 'dueDate':
      itemValue = item.dueDate;
      break;
    case 'priority':
      itemValue = (item as any).priority;
      break;
    default:
      itemValue = (item as any)[field];
  }

  // Apply operator
  switch (operator) {
    case 'eq':
      return itemValue === value;
    case 'neq':
      return itemValue !== value;
    case 'contains':
      return String(itemValue).toLowerCase().includes(String(value).toLowerCase());
    case 'in':
      if (Array.isArray(itemValue)) {
        return Array.isArray(value) ? value.some(v => itemValue.includes(v)) : itemValue.includes(value);
      }
      return Array.isArray(value) ? value.includes(itemValue) : false;
    case 'notin':
      if (Array.isArray(itemValue)) {
        return Array.isArray(value) ? !value.some(v => itemValue.includes(v)) : !itemValue.includes(value);
      }
      return Array.isArray(value) ? !value.includes(itemValue) : true;
    case 'isNull':
      return itemValue === null || itemValue === undefined;
    case 'isNotNull':
      return itemValue !== null && itemValue !== undefined;
    default:
      return itemValue === value;
  }
}

// Group items by column
function groupItemsByColumn(
  items: ProcessInstance[],
  columns: KanbanColumn[]
): Map<string, ProcessInstance[]> {
  const grouped = new Map<string, ProcessInstance[]>();

  // Initialize all columns
  columns.forEach(col => grouped.set(col.id, []));

  // Assign items to columns
  items.forEach(item => {
    for (const column of columns) {
      if (matchesFilter(item, column.filterConfig)) {
        grouped.get(column.id)?.push(item);
        break; // Item goes to first matching column
      }
    }
  });

  return grouped;
}

export default function CustomKanbanBoard({
  view,
  items,
  loading,
  onItemClick,
  onItemMove,
  onAddItem,
  onViewChange,
}: CustomKanbanBoardProps) {
  const queryClient = useQueryClient();
  const boardRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const newColumnInputRef = useRef<any>(null);

  const columns = view.columns || [];
  const cardSettings = view.cardSettings || {};
  const boardSettings = view.boardSettings || {};

  // Initialize column widths from view
  useEffect(() => {
    const widths: Record<string, number> = {};
    columns.forEach(col => {
      widths[col.id] = columnWidths[col.id] || col.width || 300;
    });
    setColumnWidths(widths);
  }, [columns]);

  // Group items by column
  const groupedItems = useMemo(() => {
    return groupItemsByColumn(items, columns);
  }, [items, columns]);

  // Calculate responsive column width
  const getColumnWidth = useCallback((column: KanbanColumn) => {
    const customWidth = columnWidths[column.id];
    if (customWidth) return customWidth;

    // Auto-size based on available space
    if (boardRef.current && columns.length > 0) {
      const boardWidth = boardRef.current.clientWidth;
      const gap = boardSettings.columnGap || 16;
      const totalGap = gap * (columns.length - 1);
      const availableWidth = boardWidth - totalGap - 32; // 32px padding
      const autoWidth = Math.floor(availableWidth / columns.length);
      return Math.max(column.minWidth || 200, Math.min(autoWidth, column.maxWidth || 500));
    }

    return column.width || 300;
  }, [columnWidths, columns, boardSettings.columnGap]);

  // Handle column resize
  const handleResizeStart = useCallback((e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    setResizingColumn(columnId);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = columnWidths[columnId] || 300;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX.current;
      const column = columns.find(c => c.id === columnId);
      const newWidth = Math.max(
        column?.minWidth || 200,
        Math.min(column?.maxWidth || 600, resizeStartWidth.current + delta)
      );
      setColumnWidths(prev => ({ ...prev, [columnId]: newWidth }));
    };

    const handleMouseUp = async () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setResizingColumn(null);

      // Save width to server
      const newWidth = columnWidths[columnId];
      if (newWidth) {
        try {
          await kanbanApi.updateColumn(columnId, { width: newWidth });
        } catch {
          // Silent fail for width save
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths, columns]);

  // Handle inline rename
  const handleStartRename = useCallback((column: KanbanColumn) => {
    setEditingColumnId(column.id);
    setEditingName(column.name);
  }, []);

  const handleFinishRename = useCallback(async () => {
    if (!editingColumnId || !editingName.trim()) {
      setEditingColumnId(null);
      return;
    }

    try {
      await kanbanApi.updateColumn(editingColumnId, { name: editingName.trim() });
      queryClient.invalidateQueries({ queryKey: ['kanban-view', view.id] });
      onViewChange?.();
    } catch {
      message.error('Failed to rename column');
    }
    setEditingColumnId(null);
  }, [editingColumnId, editingName, view.id, queryClient, onViewChange]);

  // Handle drag and drop
  const handleDragStart = useCallback((e: React.DragEvent, itemId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
    setDraggedItem(itemId);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, toColumnId: string) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData('text/plain');
    setDraggedItem(null);
    setDragOverColumn(null);

    if (!itemId || !onItemMove) return;

    // Find which column the item came from
    let fromColumnId: string | null = null;
    for (const [colId, colItems] of groupedItems) {
      if (colItems.some(item => item.id === itemId)) {
        fromColumnId = colId;
        break;
      }
    }

    if (fromColumnId && fromColumnId !== toColumnId) {
      try {
        await onItemMove(itemId, fromColumnId, toColumnId);
        message.success('Item moved');
      } catch {
        message.error('Failed to move item');
      }
    }
  }, [groupedItems, onItemMove]);

  // Column menu actions
  const getColumnMenuItems = useCallback((column: KanbanColumn): MenuProps['items'] => [
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: 'Rename',
      onClick: () => handleStartRename(column),
    },
    {
      key: 'color',
      icon: <SettingOutlined />,
      label: 'Change Color',
      children: COLUMN_COLORS.map(c => ({
        key: c.value,
        label: (
          <Space>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: c.value }} />
            {c.name}
          </Space>
        ),
        onClick: async () => {
          await kanbanApi.updateColumn(column.id, { color: c.value });
          queryClient.invalidateQueries({ queryKey: ['kanban-view', view.id] });
          onViewChange?.();
        },
      })),
    },
    { type: 'divider' },
    {
      key: 'duplicate',
      icon: <CopyOutlined />,
      label: 'Duplicate',
      onClick: async () => {
        await kanbanApi.createColumn(view.id, {
          name: `${column.name} (copy)`,
          color: column.color,
          columnType: column.columnType,
          filterConfig: column.filterConfig || undefined,
          dropAction: column.dropAction,
        });
        queryClient.invalidateQueries({ queryKey: ['kanban-view', view.id] });
        onViewChange?.();
      },
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      danger: true,
      onClick: async () => {
        await kanbanApi.deleteColumn(column.id);
        queryClient.invalidateQueries({ queryKey: ['kanban-view', view.id] });
        onViewChange?.();
      },
    },
  ], [handleStartRename, queryClient, view.id, onViewChange]);

  // Start adding new column
  const handleStartAddColumn = useCallback(() => {
    setIsAddingColumn(true);
    setNewColumnName('');
    setTimeout(() => newColumnInputRef.current?.focus(), 50);
  }, []);

  // Finish adding new column
  const handleFinishAddColumn = useCallback(async () => {
    if (!newColumnName.trim()) {
      setIsAddingColumn(false);
      return;
    }

    try {
      await kanbanApi.createColumn(view.id, {
        name: newColumnName.trim(),
        color: COLUMN_COLORS[Math.floor(Math.random() * COLUMN_COLORS.length)].value,
        columnType: 'stage',
      });
      queryClient.invalidateQueries({ queryKey: ['kanban-view', view.id] });
      onViewChange?.();
      setNewColumnName('');
      setIsAddingColumn(false);
    } catch {
      message.error('Failed to add column');
    }
  }, [newColumnName, view.id, queryClient, onViewChange]);

  // Cancel adding column
  const handleCancelAddColumn = useCallback(() => {
    setIsAddingColumn(false);
    setNewColumnName('');
  }, []);

  if (loading) {
    return (
      <div className="custom-kanban-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="custom-kanban-empty">
        <Empty
          description="No columns configured"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={handleStartAddColumn}>
            Add Column
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div
      className="custom-kanban-board"
      ref={boardRef}
      style={{
        background: boardSettings.backgroundColor || 'var(--surface-1, #fff)',
        gap: boardSettings.columnGap || 16,
      }}
    >
      {columns.map((column) => {
        const columnItems = groupedItems.get(column.id) || [];
        const isOverLimit = column.wipLimit && columnItems.length > column.wipLimit;
        const columnWidth = getColumnWidth(column);

        return (
          <div
            key={column.id}
            className={`custom-kanban-column ${dragOverColumn === column.id ? 'drag-over' : ''} ${isOverLimit ? 'over-limit' : ''}`}
            style={{
              width: columnWidth,
              minWidth: column.minWidth || 200,
              maxWidth: column.maxWidth || 600,
              borderTopColor: column.color,
            }}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Column Header */}
            <div className="custom-kanban-column-header">
              <div className="column-header-content">
                {editingColumnId === column.id ? (
                  <Input
                    size="small"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleFinishRename}
                    onPressEnter={handleFinishRename}
                    autoFocus
                    style={{ width: '100%' }}
                  />
                ) : (
                  <div className="column-title-row">
                    <Title
                      level={5}
                      className="column-title"
                      onClick={() => handleStartRename(column)}
                    >
                      {column.name}
                    </Title>
                    {column.columnType === 'filter' && (
                      <Tooltip title="Filter-based column">
                        <FilterOutlined style={{ fontSize: 12, color: 'var(--text-tertiary)' }} />
                      </Tooltip>
                    )}
                  </div>
                )}
                <Space size={4}>
                  {boardSettings.showColumnCounts !== false && (
                    <Badge
                      count={columnItems.length}
                      style={{
                        backgroundColor: isOverLimit ? 'var(--status-error)' : 'var(--border-default)',
                        color: isOverLimit ? '#fff' : 'var(--text-secondary)',
                      }}
                      showZero
                    />
                  )}
                  {column.wipLimit && boardSettings.showWipLimits && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      / {column.wipLimit}
                    </Text>
                  )}
                  <Dropdown menu={{ items: getColumnMenuItems(column) }} trigger={['click']}>
                    <Button type="text" size="small" icon={<MoreOutlined />} className="column-menu-btn" />
                  </Dropdown>
                </Space>
              </div>
            </div>

            {/* Column Content */}
            <div className="custom-kanban-column-content">
              {columnItems.map((item) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  className={`custom-kanban-card-wrapper ${draggedItem === item.id ? 'dragging' : ''}`}
                >
                  <CustomKanbanCard
                    item={item}
                    settings={cardSettings}
                    onClick={() => onItemClick(item)}
                    columnColor={column.color}
                  />
                </div>
              ))}

              {columnItems.length === 0 && (
                <div className="custom-kanban-empty-column">
                  <Text type="secondary">No items</Text>
                </div>
              )}
            </div>

            {/* Column Footer */}
            {onAddItem && (
              <div className="custom-kanban-column-footer">
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  block
                  onClick={() => onAddItem(column.id)}
                  className="add-item-btn"
                >
                  Add item
                </Button>
              </div>
            )}

            {/* Resize Handle */}
            <div
              className={`column-resize-handle ${resizingColumn === column.id ? 'resizing' : ''}`}
              onMouseDown={(e) => handleResizeStart(e, column.id)}
            />
          </div>
        );
      })}

      {/* Add Column */}
      <div className="custom-kanban-add-column">
        {isAddingColumn ? (
          <div className="add-column-input-wrapper">
            <Input
              ref={newColumnInputRef}
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              onPressEnter={handleFinishAddColumn}
              onBlur={handleFinishAddColumn}
              onKeyDown={(e) => e.key === 'Escape' && handleCancelAddColumn()}
              placeholder="Column name..."
              autoFocus
              style={{ width: 200 }}
            />
          </div>
        ) : (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleStartAddColumn}
            className="add-column-btn"
          >
            Add Column
          </Button>
        )}
      </div>
    </div>
  );
}
